import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    MNOChannel,
    PaymentProvider,
    PixAddressType,
    PixApiProvider,
    TranslationScopes,
} from "@finbackoffice/enums";
import { ISiteConfigs } from "@finbackoffice/clientbff-client";
import { ConfigContextV2, ExchangeRatesContextV2 } from "contexts";
import { useTranslation } from "react-i18next";
import {
    ClientBFFContext,
    TolaWalletDataContext,
    getCurrencyConfig,
    round,
    formatAmount,
    convertToCurrencyFormatterConfig,
} from "@finbackoffice/site-core";

export const useFirstStepWithdrawalTolaV2 = ({
    balance,
    mnoChannel,
}: {
    balance: string;
    mnoChannel?: MNOChannel;
}) => {
    const { t } = useTranslation(TranslationScopes.TolaWallet);
    const client = useContext(ClientBFFContext);
    const { setWithdrawalStep, setWithdrawalData } = useContext(TolaWalletDataContext);
    const { siteConfigs, siteDefaultCurrency, sitePaymentsCurrencyConfig } =
        useContext(ConfigContextV2);
    const { exchangeRates: rates } = useContext(ExchangeRatesContextV2);
    const currencyConfig = getCurrencyConfig(siteConfigs as ISiteConfigs, siteDefaultCurrency);
    const paymentsCurrencyConfig = sitePaymentsCurrencyConfig?.[siteDefaultCurrency];
    const { min, max_usd } = currencyConfig.withdrawal || {};
    const [errorApi, setErrorApi] = useState<string>("");
    const [exceededLimit, setExceededLimit] = useState<string>("");
    const [amount, setAmount] = useState<string>(min ? `${min}` : "");
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [recipientData, setRecipientData] = useState<{
        type: PixAddressType;
        value: string;
        error?: string;
    }>({
        type: PixAddressType.CPF,
        value: "",
        error: "",
    });
    const currentCurrencyRate = rates?.[siteDefaultCurrency]?.find(
        (r) => r.toCurrency === "USD",
    )?.rate;
    const max = currentCurrencyRate && max_usd ? max_usd / Number(currentCurrencyRate) : 0;
    const roundedMax = round(max, currencyConfig.digits);
    const maxWithdrawal = Math.min(Number(balance), roundedMax);

    const withdrawalProviders = useMemo(() => {
        const providersData = (siteConfigs as ISiteConfigs).payments.PixAPI?.withdrawal || {};
        return (Object.keys(providersData) as PixApiProvider[])
            .map((provider) => ({
                provider,
                ...providersData[provider]!,
            }))
            .filter((p) => p.enabled)
            .sort((a, b) => a.order - b.order);
    }, [siteConfigs]);

    const [selectedOperator, setSelectedOperator] = useState<MNOChannel | undefined>(mnoChannel);

    useEffect(() => {
        setErrorApi("");
    }, [amount, selectedOperator]);

    useEffect(() => {
        const numberValue = Number(amount || 0);
        if (!amount) {
            setError("");
            return;
        }
        if (numberValue < (min ?? 0) || numberValue > maxWithdrawal) {
            setError(
                t("min_max_validation", {
                    min: formatAmount({
                        format:
                            (paymentsCurrencyConfig &&
                                convertToCurrencyFormatterConfig(
                                    siteDefaultCurrency,
                                    paymentsCurrencyConfig,
                                )) ??
                            null,
                        amount: (min ?? 0).toString(),
                        withCode: false,
                    }),
                    max: formatAmount({
                        format:
                            (paymentsCurrencyConfig &&
                                convertToCurrencyFormatterConfig(
                                    siteDefaultCurrency,
                                    paymentsCurrencyConfig,
                                )) ??
                            null,
                        amount: maxWithdrawal.toString(),
                        withCode: false,
                    }),
                }),
            );
        } else {
            setError("");
        }
    }, [amount, min, maxWithdrawal, t, paymentsCurrencyConfig, siteDefaultCurrency]);

    const clickWithdrawal = useCallback(async () => {
        if (!selectedOperator) return;
        setErrorApi("");
        setExceededLimit("");
        try {
            setLoading(true);
            const response = await client.createWithdrawal({
                provider: PaymentProvider.TolaAPI,
                currency: siteDefaultCurrency,
                amount,
                provider_data: {
                    channel: selectedOperator,
                },
            });
            setWithdrawalData(response);
            setWithdrawalStep("complete");
        } catch (e: any) {
            const errors = e.response.data?.details ?? {};
            if (errors.max_24h_count_withdrawal_error) {
                setExceededLimit(errors.max_24h_count_withdrawal_error);
                return;
            }
            if (e.response.data?.error === "wallet_transient_exception") {
                setErrorApi(t("error_wallet_transient_exception"));
                return;
            }
            if (errors.error_message) {
                setErrorApi(errors.error_message);
                return;
            }
            setErrorApi(e.response.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [
        amount,
        client,
        selectedOperator,
        setWithdrawalData,
        setWithdrawalStep,
        siteDefaultCurrency,
        t,
    ]);

    return useMemo(
        () => ({
            clickWithdrawal,
            amount,
            error,
            loading,
            min,
            setAmount,
            recipientData,
            setRecipientData,
            errorApi,
            exceededLimit,
            withdrawalProviders,
            selectedOperator,
            setSelectedOperator,
        }),
        [
            clickWithdrawal,
            amount,
            error,
            loading,
            setAmount,
            min,
            recipientData,
            setRecipientData,
            errorApi,
            exceededLimit,
            withdrawalProviders,
            selectedOperator,
            setSelectedOperator,
        ],
    );
};
